// Pairing keys on /devices (TOM-471, ADR 0326): a key an owner makes once and
// a development device trades for a sign-in, so a simulator never needs a
// pairing code. Making one needs a sign-in from the last ten minutes; the key
// is shown once; revoking it signs out every device it opened.
//
// PairingKeysView draws every state from props (tested under Node, as the web
// package has no DOM test environment); PairingKeys owns the requests.

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';

export interface PairingKeySummary {
  id: string;
  label: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  devices: number;
}

export interface MadePairingKey {
  id: string;
  key: string;
  label: string;
}

interface ApiError {
  status?: number;
  body?: { error?: string; message?: string };
  message?: string;
}

function formatWhen(iso: string | null): string {
  if (iso === null) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function devicesSignedIn(n: number): string {
  return n === 1 ? '1 device signed in' : `${n} devices signed in`;
}

/** What the browser asks before a key is revoked. */
export function revokeKeyConfirmation(key: Pick<PairingKeySummary, 'label' | 'devices'>): string {
  const signsOut =
    key.devices === 0
      ? 'No device is signed in with it.'
      : `It signs out the ${key.devices === 1 ? 'device' : `${key.devices} devices`} signed in with it.`;
  return `Revoke the pairing key “${key.label}”? ${signsOut}`;
}

export interface PairingKeysViewProps {
  keys: PairingKeySummary[] | null;
  /** The key just made: shown once, until the owner dismisses it. */
  made: MadePairingKey | null;
  label: string;
  making: boolean;
  /** Making a key needs a fresh sign-in (403 reauth_required). */
  needsSignIn: boolean;
  error: string | null;
  copied: boolean;
  revokingId: string | null;
  onLabel: (label: string) => void;
  onMake: (e: FormEvent) => void;
  onCopy: () => void;
  onDismiss: () => void;
  onSignInAgain: () => void;
  onRevoke: (key: PairingKeySummary) => void;
}

export function PairingKeysView(props: PairingKeysViewProps) {
  const { keys, made } = props;
  return (
    <section className="profile-section devices-keys" data-testid="pairing-keys">
      <h3 className="profile-section-heading">Pairing keys</h3>
      <p className="profile-section-help">
        A pairing key signs a development device, such as the iOS simulator, in to your account
        without a pairing code. Anyone who holds the key can sign in as you until you revoke it.
      </p>

      {props.error !== null && (
        <ul className="form-errors" data-testid="keys-error">
          <li>{props.error}</li>
        </ul>
      )}

      {props.needsSignIn && (
        <div className="devices-key-signin" role="alert" data-testid="keys-sign-in">
          <p>
            To make a pairing key, sign in again, then come back to Linked devices and make it
            within ten minutes.
          </p>
          <button type="button" className="btn btn-primary" onClick={props.onSignInAgain}>
            Sign in again
          </button>
        </div>
      )}

      {made !== null ? (
        <div className="tb-card devices-key-made" data-testid="key-made">
          <p className="devices-key-made__title">
            <strong>{made.label}</strong>: copy the key now. It won’t be shown again.
          </p>
          <code className="devices-key-made__key" data-testid="key-value">
            {made.key}
          </code>
          <p className="devices-list__meta">
            Store it like a password. For the iOS simulator, add it to{' '}
            <code>~/.config/tombolo/ios-pairing-keys</code> as <code>host=key</code>.
          </p>
          <div className="devices-key-made__actions">
            <button type="button" className="btn btn-primary" onClick={props.onCopy}>
              {props.copied ? 'Copied' : 'Copy key'}
            </button>
            <button type="button" className="btn" onClick={props.onDismiss}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="devices-key-form" onSubmit={props.onMake}>
          <div className="form-group">
            <label htmlFor="pairing-key-label">Name</label>
            <input
              id="pairing-key-label"
              className="form-input"
              type="text"
              maxLength={80}
              placeholder="Mac simulators"
              value={props.label}
              onChange={(e) => props.onLabel(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={props.making || props.label.trim().length === 0}
          >
            {props.making ? 'Making…' : 'Make a key'}
          </button>
        </form>
      )}

      {keys !== null && keys.length > 0 && (
        <ul className="tb-list devices-list" data-testid="pairing-key-list">
          {keys.map((k) => (
            <li key={k.id} className="tb-list__row" data-testid="pairing-key">
              <span className="tb-list__body">
                <span className="devices-list__text">
                  <strong className="devices-list__name">{k.label}</strong>
                  <span className="devices-list__meta">
                    <code>{k.prefix}…</code> · made {formatWhen(k.createdAt)} · last used{' '}
                    {formatWhen(k.lastUsedAt)} · {devicesSignedIn(k.devices)}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn devices-revoke"
                  onClick={() => props.onRevoke(k)}
                  disabled={props.revokingId === k.id}
                >
                  {props.revokingId === k.id ? 'Revoking…' : 'Revoke'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The section on /devices. `onDevicesChanged` reloads the device list after a revoke. */
export function PairingKeys({ onDevicesChanged }: { onDevicesChanged: () => void }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState<PairingKeySummary[] | null>(null);
  const [made, setMade] = useState<MadePairingKey | null>(null);
  const [label, setLabel] = useState('');
  const [making, setMaking] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ keys: PairingKeySummary[] }>('/v1/device/keys');
      setKeys(res.keys);
    } catch (err) {
      const e = err as ApiError;
      setError(e.body?.message ?? e.message ?? 'Couldn’t load your pairing keys.');
      setKeys([]);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  async function make(e: FormEvent) {
    e.preventDefault();
    setMaking(true);
    setError(null);
    setNeedsSignIn(false);
    try {
      const res = await api<MadePairingKey>('/v1/device/keys', {
        method: 'POST',
        body: { label: label.trim() },
      });
      setMade({ id: res.id, key: res.key, label: res.label });
      setLabel('');
      await load();
    } catch (err) {
      const e = err as ApiError;
      if (e.body?.error === 'reauth_required') setNeedsSignIn(true);
      else setError(e.body?.message ?? e.message ?? 'Couldn’t make a pairing key.');
    } finally {
      setMaking(false);
    }
  }

  function copy() {
    if (made === null || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(made.key)
      .then(() => {
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard denied: the key is still on screen to select by hand.
      });
  }

  function dismiss() {
    setMade(null);
    setCopied(false);
  }

  async function signInAgain() {
    await logout();
    navigate('/login', { state: { from: '/devices' } });
  }

  async function revoke(key: PairingKeySummary) {
    if (typeof window !== 'undefined' && !window.confirm(revokeKeyConfirmation(key))) return;
    setRevokingId(key.id);
    setError(null);
    try {
      await api(`/v1/device/keys/${key.id}/revoke`, { method: 'POST' });
      if (made?.id === key.id) dismiss();
      await load();
      onDevicesChanged();
    } catch (err) {
      const e = err as ApiError;
      setError(e.body?.message ?? e.message ?? 'Couldn’t revoke that key.');
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <PairingKeysView
      keys={keys}
      made={made}
      label={label}
      making={making}
      needsSignIn={needsSignIn}
      error={error}
      copied={copied}
      revokingId={revokingId}
      onLabel={setLabel}
      onMake={(e) => void make(e)}
      onCopy={copy}
      onDismiss={dismiss}
      onSignInAgain={() => void signInAgain()}
      onRevoke={(k) => void revoke(k)}
    />
  );
}
